/**
 * DirtyStateDialog — warns user before discarding unsaved form changes.
 *
 * Appears when:
 * - User clicks "Cancelar" while isDirty=true
 * - User clicks backdrop in sidebar mode while isDirty=true
 * - User clicks a different row while form has unsaved changes
 *
 * Pixel-matches MasterCrud's own unsaved-changes dialog (MasterCrud.tsx:1989-2020):
 * warning icon, bold title, "Campos modificados" diff list, and the same
 * plain/outline/default button hierarchy (Seguir editando / Descartar / Guardar).
 *
 * "Continuar editando": dismisses dialog, form stays open.
 * "Descartar cambios": closes form, discards all unsaved changes.
 *
 * @module components/modals/DirtyStateDialog
 * @see FMPV-MVWC-E003-S004 I-002
 */
import React from 'react';
import { Button } from '../../../Button/Button';

export interface DirtyStateChange {
  label: string;
  from: unknown;
  to: unknown;
}

export interface DirtyStateDialogProps {
  isOpen: boolean;
  onContinue: () => void;
  onDiscard: () => void;
  /** "Guardar y salir" — submits the form, which closes it on success. */
  onSaveAndExit: () => void;
  /** Fields that changed since the form opened — shown as a "from → to" list. */
  changes?: DirtyStateChange[];
  /** Translation function override — falls back to the current Spanish text when omitted. */
  t?: (key: string, defaultValue?: string) => string;
}

function formatValue(v: unknown, emptyLabel: string): React.ReactNode {
  if (v === '' || v === undefined || v === null) {
    return <span className="italic text-content-secondary dark:text-dark-content-secondary">({emptyLabel})</span>;
  }
  // Fields with a custom renderCell (lookups resolving an id to a name, etc.) arrive here
  // already resolved to a ReactNode — render it as-is instead of String()-ing it, which
  // would print "[object Object]" for anything beyond plain text/numbers.
  if (React.isValidElement(v)) {
    return <span className="font-medium text-content-primary dark:text-dark-content-primary">{v}</span>;
  }
  return <span className="font-medium text-content-primary dark:text-dark-content-primary">{String(v)}</span>;
}

export function DirtyStateDialog({ isOpen, onContinue, onDiscard, onSaveAndExit, changes = [], t }: DirtyStateDialogProps) {
  const tt = t ?? ((key: string, defaultValue?: string) => defaultValue ?? key);
  // Always rendered in DOM to avoid animation jank (R-002)
  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="dirty-dialog-title"
    >
      <div className="absolute inset-0 bg-black/50" onClick={onContinue} />
      <div
        data-testid="dirty-state-dialog"
        className="relative w-full max-w-[35rem] bg-bg-primary dark:bg-dark-bg-primary border border-border-primary dark:border-dark-border-primary rounded-xl shadow-2xl overflow-hidden"
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 flex-shrink-0 rounded-full bg-[#fef9c3] dark:bg-yellow-900/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-[#a16207]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
            </div>
            <div className="min-w-0 flex-1">
              <h2
                id="dirty-dialog-title"
                className="text-lg font-bold text-content-primary dark:text-dark-content-primary"
              >
                {tt('dirtyDialogTitle', 'Tienes cambios sin guardar')}
              </h2>
              <p className="text-sm text-content-tertiary dark:text-dark-content-tertiary mt-1 leading-5">
                {tt('dirtyDialogDesc', 'Si sales ahora perderás los siguientes cambios. ¿Deseas guardarlos antes de volver al listado?')}
              </p>

              {changes.length > 0 && (
                <div className="mt-3 rounded-lg border border-border-primary dark:border-dark-border-primary overflow-hidden">
                  <p className="px-3 py-1.5 bg-background-secondary dark:bg-dark-bg-secondary text-[11px] font-semibold uppercase tracking-[0.3px] text-content-secondary dark:text-dark-content-secondary border-b border-border-primary dark:border-dark-border-primary">
                    {tt('modifiedFields', 'Campos modificados')}
                  </p>
                  <ul
                    data-testid="dirty-dialog-changes"
                    className="max-h-[11.25rem] overflow-y-auto divide-y divide-border-secondary dark:divide-dark-border-primary"
                  >
                    {changes.map((c, i) => (
                      <li key={i} className="px-3 py-2 text-xs">
                        <p className="font-semibold text-content-primary dark:text-dark-content-primary">{c.label}</p>
                        <p className="mt-0.5 text-content-tertiary dark:text-dark-content-tertiary flex items-center gap-1.5 flex-wrap">
                          {formatValue(c.from, tt('emptyValue', 'vacío'))}
                          <svg className="w-3 h-3 text-content-secondary dark:text-dark-content-secondary flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></svg>
                          {formatValue(c.to, tt('emptyValue', 'vacío'))}
                        </p>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border-primary dark:border-dark-border-primary">
          <Button
            type="plain"
            size="base"
            className="!h-9 !px-3 !text-sm font-medium whitespace-nowrap"
            data-testid="dirty-dialog-continue"
            onClick={onContinue}
          >
            {tt('dirtyDialogContinue', 'Continuar editando')}
          </Button>
          <Button
            type="outline"
            size="base"
            className="!h-9 !px-3 !text-sm font-medium whitespace-nowrap !text-error-content !border-error-content"
            data-testid="dirty-dialog-discard"
            onClick={onDiscard}
          >
            {tt('dirtyDialogDiscard', 'Descartar y salir')}
          </Button>
          <Button
            type="default"
            size="base"
            className="!h-9 !px-3 !text-sm font-medium whitespace-nowrap"
            data-testid="dirty-dialog-save"
            onClick={onSaveAndExit}
          >
            {tt('dirtyDialogSaveExit', 'Guardar y salir')}
          </Button>
        </div>
      </div>
    </div>
  );
}
